import { z } from 'zod';
import {
  zDocketStatus,
  zDocketType,
  zPrintErrorType,
  zPrinterInterface,
  zPrinterSdk,
} from '../enums';

/**
 * Logs sent to PostHog (`log_info` / `log_warning` events, and `$exception`s sent by
 * `createLogger`). Rules: `posthog/README.md`.
 * - Keys: flat snake_case, so PostHog filters and breakdowns work on them (PostHog doesn't
 *   flatten nested properties).
 * - Sending: used only as TypeScript types (`LogData`). Never parsed, because logging must never
 *   throw.
 * - Reading: the backend `safeParse`s an event's properties into typed data.
 */

/** Printer the log is about. Keys are flat so PostHog filters/breakdowns work on them. */
export const printerLogSchema = z.object({
  printer_identifier: z.string(),
  printer_interface: zPrinterInterface,
  printer_model: z.string().nullable(),
  printer_sdk: zPrinterSdk.nullable(),
  /** From posConfig: `false` once a discovery or print attempt has marked it OFFLINE. */
  printer_online: z.boolean(),
});
export type PrinterLog = z.input<typeof printerLogSchema>;

/** Docket the log is about. */
export const docketLogSchema = z.object({
  docket_uuid: z.string(),
  docket_type: zDocketType,
  print_job_uuid: z.string().nullable(),
  prior_print_attempts: z.number(),
  was_rerouted: z.boolean(),
});
export type DocketLog = z.input<typeof docketLogSchema>;

/**
 * An order as it was at the time of the log. Any shape: an order's shape changes over time, and
 * the backend returns it through a Convex validator, which a loose object would fail.
 */
const orderSnapshotSchema = z.unknown().optional();

/**
 * Every log's extra data, by `area`: what the log is about. Members without their own keys just
 * carry `area`. Add an area or key here before sending it.
 */
export const logDataSchema = z.discriminatedUnion('area', [
  z.object({
    area: z.literal('printing'),
    ...printerLogSchema.partial().shape,
    ...docketLogSchema.partial().shape,
    docket_status: zDocketStatus.optional(),
    order_uuid: z.string().optional(),
    print_error_type: zPrintErrorType.nullable().optional(),
    /** The printer SDK's description of the error, e.g. "Printer paper is empty". */
    print_error_message: z.string().optional(),
    /** Links a print failure to the `$exception` reported for it. */
    exception_id: z.string().optional(),
    /** The printer SDK call that failed. `null` when it failed before any call. */
    sdk_call: z.literal(['connect', 'send']).nullable().optional(),
    star_error_type: z.string().optional(),
    send_attempt: z.number().optional(),
    printed: z.boolean().optional(),
    attempt_timeout_ms: z.number().optional(),
    settled_after_ms: z.number().optional(),
    buzzer_timeout_ms: z.number().optional(),
    fallback_printer_identifier: z.string().optional(),
    /** Where staff sent a failed docket's copy to print again. */
    reprint_printer_identifier: z.string().optional(),
    reprint_docket_uuid: z.string().optional(),
    docket_template_count: z.number().optional(),
    printer_count: z.number().optional(),
    processing_error: z.string().nullable().optional(),
    upload_attempts: z.number().optional(),
    has_pos_config: z.boolean().optional(),
  }),
  z.object({
    area: z.literal('printer_discovery'),
    ...printerLogSchema.partial().shape,
    printer_interface_searched: zPrinterInterface.optional(),
  }),
  z.object({
    area: z.literal('order_sync'),
    order_uuid: z.string(),
    ipad_uuid: z.string().optional(),
    ipad_name: z.string().nullable().optional(),
    is_primary_pos: z.boolean().optional(),
    uploaded_version: z.number().nullable().optional(),
    server_version: z.number().nullable().optional(),
    /** The version this register's change was made on. */
    base_order: orderSnapshotSchema,
    /** This register's copy, as it was sent. */
    unsynced_order: orderSnapshotSchema,
    server_order: orderSnapshotSchema,
    merged_order: orderSnapshotSchema,
  }),
  z.object({
    area: z.literal('online_orders'),
    order_uuid: z.string().optional(),
  }),
  z.object({
    area: z.literal('register_close'),
    order_uuid: z.string().optional(),
    closed_at: z.number().optional(),
    orders_created_after: z.number().optional(),
    pos_terminal_uuid: z.string().nullable().optional(),
    was_offline: z.boolean().optional(),
    leftovers_completed: z.number().optional(),
    leftovers_cancelled: z.number().optional(),
  }),
  z.object({
    area: z.literal('app_update'),
    /** Which over-the-air update step failed. */
    update_step: z.literal(['check', 'download']).optional(),
    update_id: z.string().optional(),
    is_update_required: z.boolean().optional(),
    error_message: z.string().optional(),
  }),
  z.object({ area: z.literal('device_link') }),
  z.object({
    area: z.literal('persistence'),
    /** The store data that failed to merge. Any shape: it may come from an older app version. */
    persisted_store_data: z.unknown().optional(),
    error_message: z.string().optional(),
  }),
  z.object({
    area: z.literal('checkout'),
    order_uuid: z.string(),
    customer_id: z.string(),
    developer_error: z.string().nullable(),
  }),
  z.object({
    area: z.literal('payments'),
    error_message: z.string().optional(),
    stripe_error_code: z.string().optional(),
    stripe_decline_code: z.string().nullable().optional(),
  }),
  z.object({
    area: z.literal('ui'),
    component_stack: z.string().optional(),
  }),
  z.object({
    area: z.literal('dev_tools'),
    error_message: z.string().optional(),
  }),
  /** A super admin, or venue staff for a flag in their plan, turned a venue's flag on or off. */
  z.object({
    area: z.literal('feature_flags'),
    feature_flag_key: z.string(),
    is_enabled: z.boolean(),
  }),
  /**
   * A venue's plan changed: set by a super admin, or venue staff changed their subscription.
   * `null` is no plan.
   */
  z.object({
    area: z.literal('restaurant_plans'),
    brand: z.string(),
    plan_key: z.string().nullable(),
    previous_plan_key: z.string().nullable(),
  }),
]);
/** The extra data a log is sent with. `area` is required, so every log is put in a category. */
export type LogData = z.input<typeof logDataSchema>;
